import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API } from "../../../utils/api";
import "./GetQuote.css";

const STEPS = [
  { id: 0, label: "Client Details", icon: "01" },
  { id: 1, label: "Project Details", icon: "02" },
  { id: 2, label: "Terms", icon: "03" },
  { id: 3, label: "Book Session", icon: "04" },
];

const SERVICES = [
  "Software Development",
  "UI/UX Design",
  "Social Media Management",
  "SEO",
  "Mentorship & Training",
  "Ready-Made Solutions",
];

const BUDGETS = [
  "Under KSh 50,000",
  "KSh 50,000 – 150,000",
  "KSh 150,000 – 500,000",
  "KSh 500,000 – 1,500,000",
  "KSh 1,500,000+",
  "Not sure yet",
];

const TIMELINES = [
  "1 – 2 weeks (Rush — extra cost applies)",
  "3 – 4 weeks (Standard)",
  "1 – 2 months",
  "3 – 6 months",
  "Flexible / No fixed deadline",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Small fallback used only if /api/country-codes can't be reached
// (offline, backend cold-start). Field names match validate.py's
// response shape (name / code / calling_code / flag) so no mapping
// is needed once the real list loads.
const FALLBACK_COUNTRIES = [
  { name: "Kenya", code: "KE", calling_code: "+254", flag: "🇰🇪" },
  { name: "United States", code: "US", calling_code: "+1", flag: "🇺🇸" },
  { name: "United Kingdom", code: "GB", calling_code: "+44", flag: "🇬🇧" },
];

const TERMS = `RASTEGHUB — CLIENT ENGAGEMENT TERMS

1. CONSULTATION FEE
A consultation fee of KSh 5,000 is payable before any discovery or brainstorming session takes place. This fee covers our team's time, preparation, and the structured session itself.

If you choose NOT to proceed with the project after the session, the consultation fee is non-refundable — it compensates for the time and expertise invested.

If you DO choose to proceed with the project, the consultation fee is fully deducted from your final project price. For example: if your project is quoted at KSh 150,000, your final invoice will be KSh 145,000 (KSh 150,000 − KSh 5,000 consultation fee already paid).

2. PROJECT DEPOSIT
A deposit of 25% of the total agreed project price is required before any development or design work begins. This deposit is non-refundable in the event of client withdrawal after work has commenced.

3. STANDARD TIMELINE
Our standard project delivery timeline begins at 3–4 weeks for small projects and scales based on scope, complexity, and resource requirements. We commit to delivering on agreed milestones, and expect clients to provide feedback and assets within the agreed windows.

4. RUSH FEES
Projects requiring completion in under 3 weeks will attract a rush fee of 25–50% above the standard project rate, depending on urgency. All rush fees are quoted and agreed upon in writing before work begins.

5. PAYMENT SCHEDULE
· Consultation Fee — due before the discovery session
· 25% Deposit — due before development begins
· Remaining 75% — due upon project completion, before handover

For larger projects (KSh 500,000+), a milestone-based payment schedule may be agreed upon in writing.

6. REVISIONS
Each project includes up to 2 rounds of revisions per deliverable. Additional revisions are billed at our standard hourly rate. Major scope changes after work has begun will require a revised quote.

7. INTELLECTUAL PROPERTY
All intellectual property created during the engagement is owned by the client upon receipt of full and final payment. RastegHub retains the right to showcase completed work in our portfolio unless a signed non-disclosure agreement is in place.

8. CONFIDENTIALITY
All client information, business data, and project briefs are treated as strictly confidential. We do not share or sell client information to third parties under any circumstances.

9. LIMITATION OF LIABILITY
RastegHub's liability is limited to the total value of the project contract. We are not liable for indirect, incidental, or consequential damages arising from the use of delivered software or services.

10. GOVERNING LAW
These terms are governed by the laws of the Republic of Kenya. Any disputes shall first be resolved through good-faith mediation before escalating to formal legal proceedings.

By submitting this form, you confirm that you have read, understood, and agreed to all of the above terms.`;

const initialForm = {
  name: "",
  phone: "",
  phoneCountry: "KE",
  email: "",
  company: "",
  companyDesc: "",
  requirement: "",
  problemToSolve: "",
  services: [],
  budget: "",
  timeline: "",
  termsAccepted: false,
  notes: "",
};

function ProgressBar({ step }) {
  return (
    <div className="gq-progress">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div
            className={`gq-step ${i <= step ? "gq-step--done" : ""} ${i === step ? "gq-step--active" : ""}`}
          >
            <div className="gq-step-bubble">
              {i < step ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <span>{s.icon}</span>
              )}
            </div>
            <p className="gq-step-label">{s.label}</p>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={`gq-connector ${i < step ? "gq-connector--done" : ""}`}
            ></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Custom country select ───────────────────────────────────────
// A native <select> always mirrors the selected <option>'s own text
// in its closed state — there's no way to show "flag + code" closed
// but "code + name" open with a native element, so this is a small
// hand-built dropdown instead.
function CountrySelect({ countries, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const rootRef = useRef(null);

  const selected = countries.find((c) => c.code === value);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filtered = filter.trim()
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(filter.trim().toLowerCase()) ||
          c.calling_code.includes(filter.trim()) ||
          c.code.toLowerCase().includes(filter.trim().toLowerCase()),
      )
    : countries;

  return (
    <div className="gq-country-select" ref={rootRef}>
      <button
        type="button"
        className="gq-country-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{selected?.flag}</span>
        <span>{selected?.calling_code}</span>
        <svg
          className="gq-country-caret"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="12"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="gq-country-dropdown">
          <input
            type="text"
            className="gq-country-search"
            placeholder="Search country or code…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <ul className="gq-country-list" role="listbox">
            {filtered.length === 0 && (
              <li className="gq-country-empty">No matches</li>
            )}
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  className={`gq-country-option ${c.code === value ? "gq-country-option--active" : ""}`}
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                    setFilter("");
                  }}
                >
                  <span className="gq-country-option-flag">{c.flag}</span>
                  <span className="gq-country-option-code">
                    {c.calling_code}
                  </span>
                  <span className="gq-country-option-name">{c.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Debounce helper ─────────────────────────────────────────────
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function ClientDetails({ form, setForm, onNext }) {
  const emailValid = EMAIL_RE.test(form.email.trim());

  // Country codes — fetched once from /api/country-codes (validate.py),
  // falling back to a tiny hardcoded list if the request fails.
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/country-codes`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data.success || !data.countries?.length) return;
        // Pin Kenya to the top since it's the primary market —
        // everything else stays alphabetical, as the backend already sorts it.
        const ke = data.countries.find((c) => c.code === "KE");
        const rest = data.countries.filter((c) => c.code !== "KE");
        setCountries(ke ? [ke, ...rest] : data.countries);
      })
      .catch(() => {
        // Keep the fallback list — not critical enough to surface an error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Phone validation against /api/validate-phone, debounced so we're
  // not hitting the backend on every keystroke.
  const [phoneStatus, setPhoneStatus] = useState("idle"); // idle | checking | valid | invalid
  const [phoneMessage, setPhoneMessage] = useState("");
  const debouncedPhone = useDebouncedValue(form.phone, 500);

  useEffect(() => {
    const raw = debouncedPhone.trim();
    if (!raw) {
      setPhoneStatus("idle");
      setPhoneMessage("");
      return;
    }

    let cancelled = false;
    setPhoneStatus("checking");

    // If the user pasted a full international number (starts with '+'),
    // send it as-is; otherwise prefix with the selected country's dial code.
    const dial =
      countries.find((c) => c.code === form.phoneCountry)?.calling_code || "";
    const fullNumber = raw.startsWith("+") ? raw : `${dial}${raw}`;

    fetch(
      `${API}/api/validate-phone?phone=${encodeURIComponent(fullNumber)}&region=${form.phoneCountry}`,
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.valid) {
          setPhoneStatus("valid");
          setPhoneMessage("");
          // Normalize to E.164 once confirmed valid. Compared against
          // debouncedPhone (not form.phone) so this effect doesn't need
          // form.phone as a dependency — adding that would re-fire this
          // effect on every keystroke instead of only after the debounce
          // settles, which would spam the validate-phone endpoint.
          if (data.formatted && data.formatted !== debouncedPhone) {
            setForm((f) => ({ ...f, phone: data.formatted }));
          }
        } else {
          setPhoneStatus("invalid");
          setPhoneMessage(data.message || "Enter a valid phone number");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhoneStatus("invalid");
          setPhoneMessage("Could not verify number — check your connection");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedPhone, form.phoneCountry, countries, setForm]);

  // If the user types/pastes a number starting with '+', check whether
  // what they've typed so far matches a known calling code. If it does,
  // switch the selected country to match and strip the code out of the
  // visible field, leaving just the national number to keep typing.
  const handlePhoneChange = (raw) => {
    if (raw.startsWith("+")) {
      const match = countries
        .filter((c) => raw.startsWith(c.calling_code))
        // Longest calling code first — handles cases where one code is
        // a prefix of another (e.g. '+1' vs a longer NANP-style code).
        .sort((a, b) => b.calling_code.length - a.calling_code.length)[0];

      if (match) {
        const rest = raw.slice(match.calling_code.length).trimStart();
        setForm((f) => ({ ...f, phoneCountry: match.code, phone: rest }));
        return;
      }
    }
    setForm((f) => ({ ...f, phone: raw }));
  };

  const valid =
    form.name.trim() &&
    phoneStatus === "valid" &&
    emailValid &&
    form.company.trim();

  return (
    <div className="gq-tab">
      <div className="gq-tab-header">
        <h3>Tell us who you are</h3>
        <p>Your basic contact details so we know who we're working with.</p>
      </div>
      <div className="gq-fields">
        <div className="gq-field-row">
          <div className="gq-field">
            <label>
              Full Name <span>*</span>
            </label>
            <input
              type="text"
              placeholder="Jane Doe"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="gq-field">
            <label>
              Phone Number <span>*</span>
            </label>
            <div className="gq-phone-row">
              <CountrySelect
                countries={countries}
                value={form.phoneCountry}
                onChange={(code) =>
                  setForm((f) => ({ ...f, phoneCountry: code }))
                }
              />
              <input
                type="tel"
                placeholder="700 000 000"
                value={form.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                aria-invalid={phoneStatus === "invalid"}
              />
            </div>
            {phoneStatus === "checking" && (
              <p className="gq-field-hint">Checking…</p>
            )}
            {phoneStatus === "invalid" && (
              <p className="gq-field-error">{phoneMessage}</p>
            )}
          </div>
        </div>
        <div className="gq-field-row">
          <div className="gq-field">
            <label>
              Email Address <span>*</span>
            </label>
            <input
              type="email"
              placeholder="jane@company.com"
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              aria-invalid={form.email.length > 0 && !emailValid}
            />
            {form.email.length > 0 && !emailValid && (
              <p className="gq-field-error">Enter a valid email address</p>
            )}
          </div>
          <div className="gq-field">
            <label>
              Company Name <span>*</span>
            </label>
            <input
              type="text"
              placeholder="Acme Inc."
              value={form.company}
              onChange={(e) =>
                setForm((f) => ({ ...f, company: e.target.value }))
              }
            />
          </div>
        </div>
      </div>
      <div className="gq-actions">
        <button className="gq-btn-next" onClick={onNext} disabled={!valid}>
          Continue <span>→</span>
        </button>
      </div>
    </div>
  );
}

function ProjectDetails({ form, setForm, onNext, onBack }) {
  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter((x) => x !== s)
        : [...f.services, s],
    }));
  };

  const valid =
    form.companyDesc.trim() &&
    form.services.length > 0 &&
    form.requirement &&
    form.budget &&
    form.timeline;

  return (
    <div className="gq-tab">
      <div className="gq-tab-header">
        <h3>Tell us about your project</h3>
        <p>
          The more detail you share, the better we can tailor our solution for
          you.
        </p>
      </div>
      <div className="gq-fields">
        <div className="gq-field">
          <label>
            What does your company do? <span>*</span>
          </label>
          <textarea
            rows={2}
            placeholder="A brief description of your business or organisation…"
            value={form.companyDesc}
            onChange={(e) =>
              setForm((f) => ({ ...f, companyDesc: e.target.value }))
            }
          />
        </div>

        <div className="gq-field">
          <label>
            What service(s) do you need? <span>*</span>
          </label>
          <div className="gq-chips">
            {SERVICES.map((s) => (
              <button
                key={s}
                type="button"
                className={`gq-chip ${form.services.includes(s) ? "gq-chip--active" : ""}`}
                onClick={() => toggleService(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="gq-field">
          <label>
            What problem do you want us to solve? <span>*</span>
          </label>
          <textarea
            rows={3}
            placeholder="Describe the challenge, goal, or opportunity. What does success look like for you?"
            value={form.requirement}
            onChange={(e) =>
              setForm((f) => ({ ...f, requirement: e.target.value }))
            }
          />
        </div>

        <div className="gq-field-row">
          <div className="gq-field">
            <label>
              Budget Range <span>*</span>
            </label>
            <div className="gq-select-group">
              {BUDGETS.map((b) => (
                <button
                  key={b}
                  type="button"
                  className={`gq-select-opt ${form.budget === b ? "gq-select-opt--active" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, budget: b }))}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
          <div className="gq-field">
            <label>
              Preferred Timeline <span>*</span>
            </label>
            <div className="gq-select-group">
              {TIMELINES.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`gq-select-opt ${form.timeline === t ? "gq-select-opt--active" : ""} ${t.includes("Rush") ? "gq-select-opt--warning" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, timeline: t }))}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {form.timeline.includes("Rush") && (
          <div className="gq-disclaimer">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="16"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>
              <strong>Rush timeline selected.</strong> Our standard delivery
              starts at 3–4 weeks. Timelines shorter than this attract an
              additional fee of 25–50% above the project rate, depending on
              urgency. This will be discussed and agreed before work begins.
            </p>
          </div>
        )}
      </div>
      <div className="gq-actions gq-actions--split">
        <button className="gq-btn-back" onClick={onBack}>
          ← Back
        </button>
        <button className="gq-btn-next" onClick={onNext} disabled={!valid}>
          Continue <span>→</span>
        </button>
      </div>
    </div>
  );
}

function TermsTab({ form, setForm, onNext, onBack }) {
  return (
    <div className="gq-tab">
      <div className="gq-tab-header">
        <h3>Our Terms & Conditions</h3>
        <p>
          Please read carefully before proceeding. These govern our engagement.
        </p>
      </div>

      <div className="gq-terms-scroll">
        <pre className="gq-terms-text">{TERMS}</pre>
      </div>

      <label className="gq-checkbox">
        <input
          type="checkbox"
          checked={form.termsAccepted}
          onChange={(e) =>
            setForm((f) => ({ ...f, termsAccepted: e.target.checked }))
          }
        />
        <span className="gq-checkbox-box">
          {form.termsAccepted && (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </span>
        <span className="gq-checkbox-label">
          I have read and agree to RastegHub's Terms & Conditions
        </span>
      </label>

      <div className="gq-actions gq-actions--split">
        <button className="gq-btn-back" onClick={onBack}>
          ← Back
        </button>
        <button
          className="gq-btn-next"
          onClick={onNext}
          disabled={!form.termsAccepted}
        >
          Continue <span>→</span>
        </button>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────
const TIME_SLOTS = [
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtTime(t) {
  const h = parseInt(t.split(":")[0]);
  return h < 12 ? `${h}:00 AM` : h === 12 ? "12:00 PM" : `${h - 12}:00 PM`;
}

function BookSession({
  form,
  onBack,
  onSubmit,
  submitted,
  submitting,
  submitError,
}) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setDate] = useState(null);
  const [selectedTime, setTime] = useState(null);
  const [takenSlots, setTaken] = useState([]);
  const [loadingSlots, setLoading] = useState(false);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  // Bookings need at least 24 hours' notice. This is a moving cutoff —
  // e.g. if it's 3 PM today, anything before 3 PM tomorrow is too soon,
  // not just "today" as a whole day.
  const minBookableAt = today.getTime() + 24 * 60 * 60 * 1000;
  const slotTimestamp = (date, time) =>
    new Date(`${date}T${time}:00`).getTime();
  const isTooSoon = (date, time) => slotTimestamp(date, time) < minBookableAt;

  useEffect(() => {
    const y = viewYear,
      m = viewMonth;
    const start = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const last = new Date(y, m + 1, 0).getDate();
    const end = `${y}-${String(m + 1).padStart(2, "0")}-${last}`;
    setLoading(true);
    fetch(`${API}/api/bookings/taken?start=${start}&end=${end}`)
      .then((r) => r.json())
      .then((d) => setTaken(d.taken || []))
      .catch(() => setTaken([]))
      .finally(() => setLoading(false));
  }, [viewMonth, viewYear]);

  const isTaken = (date, time) =>
    takenSlots.some((s) => s.date === date && s.time === time);
  const cellDate = (day) =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const isSunday = (day) => new Date(viewYear, viewMonth, day).getDay() === 0;
  const isPast = (day) => cellDate(day) < todayStr;

  const prevMonth = () => {
    setDate(null);
    setTime(null);

    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    setDate(null);
    setTime(null);

    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // Build grid — week starts Monday
  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const startOff = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMon = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [
    ...Array(startOff).fill(null),
    ...Array.from({ length: daysInMon }, (_, i) => i + 1),
  ];

  if (submitted)
    return (
      <div className="gq-tab gq-success">
        <div className="gq-success-orb"></div>
        <span className="gq-success-icon"></span>
        <h3>You're all set, {form.name.split(" ")[0]}.</h3>
        <p>
          Your session is booked for <strong>{selectedDate}</strong> at{" "}
          <strong>{selectedTime && fmtTime(selectedTime)}</strong>. We'll
          confirm at <strong>{form.email}</strong>.
        </p>
        <p className="gq-success-note">
          We're looking forward to building something great together.
        </p>
      </div>
    );

  return (
    <div className="gq-tab">
      <div className="gq-tab-header">
        <h3>Book a Discovery Session</h3>
        <p>
          Pick a date and time — Mon to Sat, 9 AM to 6 PM. Taken slots are shown
          but unclickable.
        </p>
      </div>

      {/* Calendar */}
      <div className="gq-cal">
        <div className="gq-cal-nav-row">
          <button className="gq-cal-nav" onClick={prevMonth}>
            ‹
          </button>
          <span className="gq-cal-month-label">
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button className="gq-cal-nav" onClick={nextMonth}>
            ›
          </button>
        </div>
        <div className="gq-cal-grid">
          {DAY_LABELS.map((d) => (
            <div
              key={d}
              className={`gq-cal-head ${d === "Sun" ? "gq-cal-head--sun" : ""}`}
            >
              {d}
            </div>
          ))}
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} />;
            const ds = cellDate(day);
            const sun = isSunday(day);
            const past = isPast(day);
            const allTaken =
              !sun && !past && TIME_SLOTS.every((t) => isTaken(ds, t));
            const allTooSoon =
              !sun &&
              !past &&
              !allTaken &&
              TIME_SLOTS.every((t) => isTaken(ds, t) || isTooSoon(ds, t));
            const full = allTaken || allTooSoon;
            const disabled = sun || past || full;
            const selected = ds === selectedDate;
            return (
              <button
                key={ds}
                className={[
                  "gq-cal-day",
                  selected ? "gq-cal-day--sel" : "",
                  disabled ? "gq-cal-day--dis" : "",
                  sun ? "gq-cal-day--sun" : "",
                  full ? "gq-cal-day--full" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => !disabled && (setDate(ds), setTime(null))}
                disabled={disabled}
                title={
                  sun
                    ? "Closed on Sundays"
                    : allTaken
                      ? "Fully booked"
                      : allTooSoon
                        ? "Too soon to book — needs 24 hours notice"
                        : ""
                }
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time slots */}
      {selectedDate && (
        <div className="gq-slots">
          <p className="gq-slots-title">
            Times for <strong>{selectedDate}</strong>
            {loadingSlots && <span className="gq-slots-loading"> ···</span>}
          </p>
          <div className="gq-slots-grid">
            {TIME_SLOTS.map((t) => {
              const taken = isTaken(selectedDate, t);
              const soon = !taken && isTooSoon(selectedDate, t);
              const disabled = taken || soon;
              const active = selectedTime === t;
              return (
                <button
                  key={t}
                  className={[
                    "gq-slot-btn",
                    active ? "gq-slot-btn--active" : "",
                    taken ? "gq-slot-btn--taken" : "",
                    soon ? "gq-slot-btn--soon" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => !disabled && setTime(t)}
                  disabled={disabled}
                >
                  {fmtTime(t)}
                  {taken && <small>Taken</small>}
                  {soon && <small>Too soon</small>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {submitError && <div className="gq-submit-error"> {submitError}</div>}

      <div className="gq-actions gq-actions--split">
        <button className="gq-btn-back" onClick={onBack} disabled={submitting}>
          ← Back
        </button>
        <button
          className="gq-btn-submit"
          onClick={() => onSubmit(selectedDate, selectedTime)}
          disabled={!selectedDate || !selectedTime || submitting}
        >
          {submitting ? "Submitting…" : "Submit "}
        </button>
      </div>
    </div>
  );
}

export default function GetQuote() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Capacity check
  const location = useLocation();
  const isQuotePage = location.pathname === "/quote";
  const [capacity, setCapacity] = useState(null);
  const [capacityAcknowledged, setAck] = useState(false);
  const atCapacity =
    isQuotePage && capacity?.at_capacity && !capacityAcknowledged;

  useEffect(() => {
    // Only check capacity when on the dedicated /quote page
    if (!isQuotePage) return;
    fetch(`${API}/api/capacity`)
      .then((r) => r.json())
      .then(setCapacity)
      .catch(() => setCapacity({ at_capacity: false }));
  }, [isQuotePage]);
  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (bookingDate, bookingTime) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // 1. Submit the quote
      const res = await fetch(`${API}/api/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          company: form.company || null,
          company_desc: form.companyDesc || null,
          services: form.services,
          requirement: form.requirement,
          budget: form.budget,
          timeline: form.timeline,
          terms_accepted: form.termsAccepted,
          notes: form.notes || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Submission failed.");
      }
      const quoteData = await res.json();

      // 2. Save the booking slot
      if (bookingDate && bookingTime) {
        const bookingRes = await fetch(`${API}/api/bookings`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            quote_id: quoteData.id || null,
            name: form.name,
            email: form.email,
            booking_date: bookingDate,
            booking_time: bookingTime,
            notes: form.notes || null,
          }),
        });
        if (!bookingRes.ok) {
          const err = await bookingRes.json().catch(() => null);
          throw new Error(err?.detail || "Failed to save booking slot.");
        }
      }

      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="getquote" id="quote">
      <div className="gq-bg-grid" aria-hidden="true"></div>

      {/* ── Capacity popup ── */}
      {atCapacity && (
        <div className="gq-capacity-overlay">
          <div className="gq-capacity-card">
            <div className="gq-capacity-orb"></div>
            <span className="gq-capacity-icon"></span>
            <h3>We're at full capacity right now</h3>
            <p>
              We're currently handling{" "}
              <strong>
                {capacity.active_projects} of {capacity.max_projects}
              </strong>{" "}
              active projects — and we give each one our full attention.
            </p>
            <p>
              You're welcome to submit your brief anyway. We'll keep it on file
              and reach out as soon as a slot opens up. No commitments required.
            </p>
            <div className="gq-capacity-actions">
              <a href="/" className="gq-capacity-leave">
                Maybe later
              </a>
              <button
                className="gq-capacity-proceed"
                onClick={() => setAck(true)}
              >
                Submit anyway →
              </button>
            </div>
            <p className="gq-capacity-note">
              Want to be notified when we're free? Include your email in the
              form and we'll reach out.
            </p>
          </div>
        </div>
      )}

      <div className="gq-inner">
        <div className="gq-headline">
          <span className="gq-eyebrow">Work With Us</span>
          <h2 className="gq-title">
            Let's build
            <br />
            <em>something great.</em>
          </h2>
          <p className="gq-subtitle">
            Fill out the form below — it takes about 4 minutes — and we'll come
            back to you within 24 hours.
          </p>
        </div>

        <div className="gq-card">
          {!submitted && <ProgressBar step={step} />}

          <div className="gq-tab-body">
            {step === 0 && (
              <ClientDetails form={form} setForm={setForm} onNext={next} />
            )}
            {step === 1 && (
              <ProjectDetails
                form={form}
                setForm={setForm}
                onNext={next}
                onBack={back}
              />
            )}
            {step === 2 && (
              <TermsTab
                form={form}
                setForm={setForm}
                onNext={next}
                onBack={back}
              />
            )}
            {step === 3 && (
              <BookSession
                form={form}
                onBack={back}
                onSubmit={submit}
                submitted={submitted}
                submitting={submitting}
                submitError={submitError}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}